export type TimedGrant = {
  id: string;
  email: string;
  subject?: string;
  server_id: string;
  status: string;
  effective_status?: string;
  expires_at?: string | null;
};
export type WarningState = { stage: number; observedActive: boolean };
export function effectiveGrants<T extends TimedGrant>(
  requests: T[],
  email: string,
  subject = "",
): T[] {
  const resources = new Map<string, T>();
  for (const request of requests) {
    const status = request.effective_status ?? request.status;
    if (
      !ownsGrant(request, email, subject) ||
      request.status !== "approved" ||
      !["approved", "expired"].includes(status) ||
      !request.expires_at ||
      !Number.isFinite(Date.parse(request.expires_at))
    )
      continue;
    const previous = resources.get(request.server_id);
    if (!previous || Date.parse(request.expires_at) > Date.parse(previous.expires_at!))
      resources.set(request.server_id, request);
  }
  return [...resources.values()];
}
export function deadlineWarning(
  key: string,
  deadline: number,
  now: number,
  states: Map<string, WarningState>,
): "five" | "one" | "expired" | undefined {
  const remaining = deadline - now;
  const state = states.get(key) ?? { stage: 0, observedActive: false };
  if (remaining > 0) state.observedActive = true;
  const stage = remaining <= 0 ? 3 : remaining <= 60000 ? 2 : remaining <= 300000 ? 1 : 0;
  let result: "five" | "one" | "expired" | undefined;
  if (stage > state.stage && (stage !== 3 || state.observedActive))
    result = stage === 3 ? "expired" : stage === 2 ? "one" : "five";
  state.stage = Math.max(state.stage, stage);
  states.set(key, state);
  return result;
}
export function remainingTime(deadline: number, now: number) {
  const seconds = Math.max(0, Math.ceil((deadline - now) / 1000));
  return seconds === 0
    ? "Expired"
    : `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, "0")}s remaining`;
}
let clockServer = Date.now(),
  clockMonotonic = performance.now();
export function observeGrantClock(response: Response) {
  const parsed = Date.parse(response.headers.get("date") ?? "");
  if (Number.isFinite(parsed)) {
    clockServer = parsed;
    clockMonotonic = performance.now();
  }
}
export function grantNow() {
  return clockServer + Math.max(0, performance.now() - clockMonotonic);
}

export function ownsGrant(
  request: { email: string; subject?: string },
  email: string,
  subject = "",
) {
  return request.subject && subject ? request.subject === subject : request.email === email;
}

export function accessStatusLabel(status: string) {
  return status === "policy_changed" ? "approval no longer valid after a policy change" : status;
}
